import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../layouts/DashboardLayout';
import { createGradeSection, deleteGradeSection, listGradeSections, updateGradeSection } from '../api/gradeSections';
import { formatDateTime } from '../utils/format';
import { useSnackbar } from 'notistack';

const DEFAULT_TRACK = {
  homeLanguageOptions: ['English HL', 'Afrikaans HL'],
  falOptions: ['Afrikaans FAL', 'IsiZulu FAL'],
};

const trackLabels = {
  homeLanguageOptions: 'HL',
  falOptions: 'FAL',
};

const GradesClassesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    grade: '',
    isActive: 'all',
  });
  const [cohortYear, setCohortYear] = useState(new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState({
    year: new Date().getFullYear(),
    grade: '',
    section: 'A',
    capacity: 20,
    homeLanguageOptions: DEFAULT_TRACK.homeLanguageOptions,
    falOptions: DEFAULT_TRACK.falOptions,
    isActive: true,
    notes: '',
  });
  const { enqueueSnackbar } = useSnackbar();

  const loadSections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.grade) params.grade = filters.grade;
      if (filters.isActive !== 'all') params.isActive = filters.isActive === 'active';
      const data = await listGradeSections(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load grade sections');
    } finally {
      setLoading(false);
    }
  }, [filters.grade, filters.isActive, filters.year]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  const resetForm = useCallback((row) => {
    if (row) {
      setForm({
        year: row.year || new Date().getFullYear(),
        grade: row.grade || '',
        section: row.section || 'A',
        capacity: row.capacity || 20,
        homeLanguageOptions: row.languageTrack?.homeLanguageOptions?.length
          ? row.languageTrack.homeLanguageOptions
          : DEFAULT_TRACK.homeLanguageOptions,
        falOptions: row.languageTrack?.falOptions?.length
          ? row.languageTrack.falOptions
          : DEFAULT_TRACK.falOptions,
        isActive: row.isActive !== false,
        notes: row.notes || '',
      });
    } else {
      setForm({
        year: new Date().getFullYear(),
        grade: '',
        section: 'A',
        capacity: 20,
        homeLanguageOptions: DEFAULT_TRACK.homeLanguageOptions,
        falOptions: DEFAULT_TRACK.falOptions,
        isActive: true,
        notes: '',
      });
    }
  }, []);

  const handleOpenCreate = () => {
    setSelectedRow(null);
    resetForm(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setSelectedRow(row);
    resetForm(row);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setDialogLoading(true);
      const payload = {
        year: Number(form.year),
        grade: Number(form.grade),
        section: String(form.section || '').toUpperCase(),
        capacity: Number(form.capacity) || 20,
        languageTrack: {
          homeLanguageOptions: form.homeLanguageOptions,
          falOptions: form.falOptions,
        },
        isActive: Boolean(form.isActive),
        notes: form.notes || null,
      };

      if (selectedRow?._id) {
        await updateGradeSection(selectedRow._id, payload);
        enqueueSnackbar('Grade section updated', { variant: 'success' });
      } else {
        await createGradeSection(payload);
        enqueueSnackbar('Grade section created', { variant: 'success' });
      }

      setDialogOpen(false);
      await loadSections();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to save grade section', { variant: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(`Delete ${row.year} G${row.grade}${row.section} This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteGradeSection(row._id);
      enqueueSnackbar('Grade section deleted', { variant: 'success' });
      await loadSections();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to delete grade section', { variant: 'error' });
    }
  };

  const handleCreateCohorts = async () => {
    const year = Number(cohortYear);
    if (!year) {
      enqueueSnackbar('Enter a valid year', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const grades = Array.from({ length: 9 }, (_, i) => i + 1);
      const results = await Promise.allSettled(
        grades.map((grade) => createGradeSection({
          year,
          grade,
          section: 'A',
          capacity: 20,
          languageTrack: DEFAULT_TRACK,
          isActive: true,
          notes: null,
        }))
      );
      const created = results.filter((r) => r.status === 'fulfilled').length;
      const conflicts = results.filter((r) => r.status === 'rejected' && r.reason?.response?.status === 409).length;
      enqueueSnackbar(
 `Cohorts created: ${created}${conflicts ? `, already existed: ${conflicts}` : ''}`,
        { variant: 'success' }
      );
      await loadSections();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to create cohorts', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { field: 'year', headerName: 'Year', minWidth: 110 },
    { field: 'grade', headerName: 'Grade', minWidth: 100 },
    { field: 'section', headerName: 'Section', minWidth: 110 },
    { field: 'capacity', headerName: 'Capacity', minWidth: 110 },
    {
      field: 'languageTrack',
      headerName: 'Language Tracks',
      flex: 1,
      minWidth: 260,
      valueGetter: (params) => {
        const track = params?.row?.languageTrack || {};
        const hl = track.homeLanguageOptions?.join(', ') || '-';
        const fal = track.falOptions?.join(', ') || '-';
        return `HL: ${hl} | FAL: ${fal}`;
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => (
        <Chip
 label={params.value ? 'Active' : 'Inactive'}
 color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      minWidth: 190,
      valueGetter: (params) => (params?.row?.updatedAt ? formatDateTime(params.row.updatedAt) : '-'),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [handleDelete, handleOpenEdit]);

  return (
    <DashboardLayout
      title="Grades & Classes"
      actions={(
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Year"
            type="number"
            value={filters.year}
            onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Grade"
            type="number"
            value={filters.grade}
            onChange={(event) => setFilters((prev) => ({ ...prev, grade: event.target.value }))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={filters.isActive}
            onChange={(event) => setFilters((prev) => ({ ...prev, isActive: event.target.value }))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={loadSections}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate}>
            Add Section
          </Button>
        </Stack>
      )}
    >
      <Box>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Create Year & Grade Cohorts"
              subheader="Creates Grade 1-9 cohorts with Section A and a 20-learner capacity."
            />
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Year"
                  type="number"
                  value={cohortYear}
                  onChange={(event) => setCohortYear(event.target.value)}
                  sx={{ maxWidth: 180 }}
                />
                <Button variant="contained" onClick={handleCreateCohorts} disabled={loading}>
                  Create G1-G9 Cohorts
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Auto-sectioning adds B, C, etc. as classes fill.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader title="Grade Sections" />
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box sx={{ height: 520 }}>
                <DataGrid
                  rows={rows}
                  getRowId={(row) => row._id}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '& .MuiDataGrid-virtualScroller': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-row': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-cell': { color: 'text.primary' },
                    '& .MuiDataGrid-cellContent': { color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: 'text.primary' },
                    '& .MuiDataGrid-footerContainer': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-overlay': { color: 'text.secondary' },
                  }}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
 <DialogTitle>{selectedRow ? 'Edit Grade Section' : 'Add Grade Section'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Year"
                type="number"
                value={form.year}
                onChange={(event) => setForm((prev) => ({ ...prev, year: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Grade"
                type="number"
                value={form.grade}
                onChange={(event) => setForm((prev) => ({ ...prev, grade: event.target.value }))}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Section"
                value={form.section}
                onChange={(event) => setForm((prev) => ({ ...prev, section: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Capacity"
                type="number"
                value={form.capacity}
                onChange={(event) => setForm((prev) => ({ ...prev, capacity: event.target.value }))}
                fullWidth
              />
            </Stack>
            {(['homeLanguageOptions', 'falOptions']).map((key) => (
              <TextField
                key={key}
                select
                label={`${trackLabels[key]} Options`}
                value={form[key]}
                onChange={(event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))}
                SelectProps={{
                  multiple: true,
 renderValue: (selected) => (selected.length ? selected.join(', ') : `Select ${trackLabels[key]} options`),
                }}
              >
 {(key === 'homeLanguageOptions' ? DEFAULT_TRACK.homeLanguageOptions : DEFAULT_TRACK.falOptions)
                  .map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
              </TextField>
            ))}
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.isActive}
                  onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
              )}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={dialogLoading}>
 {dialogLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default GradesClassesPage;
